import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './Board.css';
import './BigTournamentBackgammonResponsive.css';
import './BigTournamentBottomNumbersControl.css';
import { useSound } from './sfx/SoundProvider';

// import bigTournamentLongSocket from './bigTournamentLongSocket';
import bigTournamentLongSocket, {
  ensureBigTournamentLongSocketAuthAndConnect,
  safeEmit,
} from './bigTournamentLongSocket';

import { useLocation, useNavigate } from 'react-router-dom';

const BIG_LONG_BOARD_WIDTH = 1020;
const BIG_LONG_BOARD_FALLBACK_HEIGHT = 640;

const BigTournamentBackgammonBoardDisplay = ({
  // ---- Ваши пропсы ----
  longBoardState,
  longOnPointClick,
  longHandleDragOver,
  longHandleDragStart,
  longHandleDrop,
  longFirstRolls,
  longHasRolledDice,
  firstMoveComplete,
  longCurrentTurn,
  longMyColor = 'white', // 'white' или 'black'
  longRolledDice,
  longShowUndoButton,
  longHandleUndoMove,
  longNumberMove,
  longConfirmMove,
  handleLongRollDiceClick,
  longWhiteOffBoard,
  longBlackOffBoard,
  longFirstMoveComplete,
  longLeaveTable,
  longMoveHistory,
  longRollEnabled,
  player1Time,
  player2Time,
  onResign,
  resignDisabled,
  player1Profile,
  player2Profile,
  longPlayer1Name,
  longPlayer2Name,
  tournamentStageLabel,
  tournamentPrize,
  tournamentTypeLabel,
  gameFinished = false,
  isGameFinished = false,
  showRebuyModal = false,
  showRebuyOffer = false,
  rebuyAvailable = false,
  canRebuy = false,
  rebuyAmount,
  rebuyToPrizeFund = true,
  rebuyTitle,
  rebuyDescription,
  rebuyAcceptLabel,
  rebuyDeclineLabel,
  onAcceptRebuy,
  onRebuy,
  onDeclineRebuy,
  onRejectRebuy,
  autoRoll,
  autoConfirm,
  onToggleAutoRoll,
  onToggleAutoConfirm,
  // ... и т.д.
}) => {
  const { t: translate, i18n } = useTranslation();

  /* ===================== SFX ===================== */
  const { play } = useSound();
  const playSfx = (name) => {
    try {
      const p = play?.(name);
      if (p && typeof p.then === 'function') p.catch(() => {});
    } catch (_) {}
  };

  // «goodluck» после автопервого броска — под мобильный жест
  const goodluckPlayedRef     = useRef(false);
  const gestureHandlerRef     = useRef(null);
  const suppressClickUntilRef = useRef(0);


    // 🔽 НОВОЕ: отслеживаем появление/исчезновение кубиков
  // чтобы звук игрался по факту броска, а не по клику на кнопку
  const hadDiceRef = useRef(false);
  const prevFirstRollsRef = useRef({
    player1Roll: null,
    player2Roll: null,
  });


  const location = useLocation();
  const navigate  = useNavigate();
  const [isSocketDown, setIsSocketDown] = useState(false);


  /* =========================================================
     Изолированный responsive-масштаб только большой длинной доски.
     Внутренняя геометрия остаётся 1020px и масштабируется целиком,
     поэтому пункты, шашки и области клика не расходятся.
     ========================================================= */
  const shellRef = useRef(null);
  const hudRef = useRef(null);
  const controlsRef = useRef(null);
  const boardCanvasRef = useRef(null);
  const boardImageRef = useRef(null);
  const resizeFrameRef = useRef(null);

  const [responsiveLayout, setResponsiveLayout] = useState({
    scaleX: 1,
    scaleY: 1,
    boardWidth: BIG_LONG_BOARD_WIDTH,
    boardHeight: BIG_LONG_BOARD_FALLBACK_HEIGHT,
    displayBoardHeight: BIG_LONG_BOARD_FALLBACK_HEIGHT,
    slotWidth: BIG_LONG_BOARD_WIDTH,
    slotHeight: BIG_LONG_BOARD_FALLBACK_HEIGHT,
    isLandscape: false,
    isCompact: false,
    fillsViewport: false,
  });

  const recalculateResponsiveLayout = useCallback(() => {
    if (typeof window === 'undefined') return;

    if (resizeFrameRef.current !== null) {
      window.cancelAnimationFrame(resizeFrameRef.current);
    }

    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeFrameRef.current = null;

      const visualViewport = window.visualViewport;
      const viewportWidth = Math.max(
        280,
        Math.round(visualViewport?.width || window.innerWidth || BIG_LONG_BOARD_WIDTH)
      );
      const viewportHeight = Math.max(
        240,
        Math.round(visualViewport?.height || window.innerHeight || 700)
      );

      const boardElement = boardCanvasRef.current;
      const imageElement = boardImageRef.current;

      const measuredWidth = Math.max(
        1,
        Math.round(boardElement?.offsetWidth || BIG_LONG_BOARD_WIDTH)
      );

      // Берём исходную высоту из реального соотношения Board33.png.
      // Это не даёт compact-landscape высоте зациклиться через ResizeObserver.
      const boardContentWidth = Math.max(1, measuredWidth - 40);
      const imageAspectHeight =
        imageElement?.naturalWidth > 0 && imageElement?.naturalHeight > 0
          ? Math.round(
              boardContentWidth *
              (imageElement.naturalHeight / imageElement.naturalWidth)
            ) + 40
          : BIG_LONG_BOARD_FALLBACK_HEIGHT;

      const measuredHeight = Math.max(
        1,
        imageAspectHeight || BIG_LONG_BOARD_FALLBACK_HEIGHT
      );

      const shellWidth = Math.max(
        280,
        Math.round(shellRef.current?.clientWidth || viewportWidth)
      );

      const isLandscape = viewportWidth > viewportHeight;

      // Compact layout may still be useful for tablets and small windows,
      // but fullscreen board/HUD is strictly phone-landscape only.
      const isCompact = viewportWidth <= 1200 || viewportHeight <= 700;
      const isPhoneLandscape =
        isLandscape &&
        viewportWidth <= 1100 &&
        viewportHeight <= 600;

      const fillsViewport = isPhoneLandscape;

      let scaleX = 1;
      let scaleY = 1;
      let displayBoardHeight = measuredHeight;
      let slotWidth = measuredWidth;
      let slotHeight = measuredHeight;

      if (fillsViewport) {
        // В landscape используем ОДИН масштаб по X и Y.
        // Поэтому изображение, шашки и треугольники больше не сплющиваются.
        // Чтобы при этом доска заняла весь широкий экран, мы не растягиваем
        // картинку, а аккуратно вырезаем часть пустой середины доски:
        // верх и низ Board33.png остаются в исходных пропорциях.
        const uniformScale = viewportWidth / measuredWidth;
        const compactHeight = viewportHeight / uniformScale;

        scaleX = uniformScale;
        scaleY = uniformScale;
        displayBoardHeight = Math.max(
          400,
          Math.min(measuredHeight, compactHeight)
        );
        slotWidth = viewportWidth;
        slotHeight = viewportHeight;
      } else {
        const horizontalReserve = isCompact ? 12 : 40;
        const availableWidth = Math.max(
          240,
          Math.min(viewportWidth, shellWidth) - horizontalReserve
        );

        /*
         * DESKTOP HEIGHT FIT — только большая турнирная доска длинных нард.
         *
         * Раньше desktop (>1200px) ограничивался только по ширине. Поэтому
         * при окне вроде 1498x861 ширины хватало на scale=1, но высота доски
         * + HUD становилась больше viewport и появлялся вертикальный скролл.
         *
         * Для compact сохраняем прежнее поведение, кроме узкой границы
         * 1101–1200px landscape: там тоже нужен контроль по высоте.
         */
        const widthScale = availableWidth / measuredWidth;

        let heightScale = 1;

        // В compact landscape одной проверки по ширине недостаточно.
        // В зоне примерно 986..1200px widthScale уже меньше 1, но всё ещё
        // недостаточно мал, чтобы доска + HUD целиком вошли по высоте.
        // Поэтому для ВСЕГО обычного compact-landscape (>600px по высоте)
        // одновременно учитываем и ширину, и высоту.
        //
        // Это касается только этой большой турнирной доски длинных нард.
        // Phone-landscape (<=600px по высоте) остаётся в своей отдельной
        // fullscreen-логике выше и сюда не попадает.
        const compactNeedsHeightFit =
          isCompact &&
          isLandscape &&
          viewportHeight > 600;

        const needsContainedHeightFit =
          !isCompact || compactNeedsHeightFit;

        if (needsContainedHeightFit) {
          const shellRect = shellRef.current?.getBoundingClientRect();
          const hudRect = hudRef.current?.getBoundingClientRect();

          const shellTop = Math.max(
            0,
            Math.round(shellRect?.top || 0)
          );

          const hudHeight = Math.max(
            0,
            Math.round(hudRect?.height || 0)
          );

          // 18px — безопасный зазор между HUD/доской и нижним краем viewport.
          const desktopVerticalReserve = 18;

          const availableBoardHeight = Math.max(
            240,
            viewportHeight - shellTop - hudHeight - desktopVerticalReserve
          );

          heightScale = availableBoardHeight / measuredHeight;
        }

        const uniformScale = Math.max(
          0.22,
          Math.min(1, widthScale, heightScale)
        );

        scaleX = uniformScale;
        scaleY = uniformScale;
        slotWidth = Math.max(1, Math.round(measuredWidth * uniformScale));
        slotHeight = Math.max(1, Math.round(measuredHeight * uniformScale));
      }

      const roundedScaleX = Math.round(scaleX * 10000) / 10000;
      const roundedScaleY = Math.round(scaleY * 10000) / 10000;

      setResponsiveLayout(previous => {
        const unchanged =
          Math.abs(previous.scaleX - roundedScaleX) < 0.0001 &&
          Math.abs(previous.scaleY - roundedScaleY) < 0.0001 &&
          previous.boardWidth === measuredWidth &&
          previous.boardHeight === measuredHeight &&
          Math.abs(previous.displayBoardHeight - displayBoardHeight) < 0.5 &&
          previous.slotWidth === slotWidth &&
          previous.slotHeight === slotHeight &&
          previous.isLandscape === isLandscape &&
          previous.isCompact === isCompact &&
          previous.fillsViewport === fillsViewport;

        if (unchanged) return previous;

        return {
          scaleX: roundedScaleX,
          scaleY: roundedScaleY,
          boardWidth: measuredWidth,
          boardHeight: measuredHeight,
          displayBoardHeight,
          slotWidth,
          slotHeight,
          isLandscape,
          isCompact,
          fillsViewport,
        };
      });
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const schedule = () => recalculateResponsiveLayout();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(schedule)
        : null;

    [
      shellRef.current,
      boardCanvasRef.current,
    ].forEach(element => {
      if (element && resizeObserver) {
        resizeObserver.observe(element);
      }
    });

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });

    schedule();
    const delayedMeasure = window.setTimeout(schedule, 180);

    return () => {
      window.clearTimeout(delayedMeasure);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);

      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [recalculateResponsiveLayout]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!responsiveLayout.fillsViewport) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyMargin = body.style.margin;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.margin = '0';

    // Класс нужен, чтобы любые модальные окна приложения были выше
    // полноэкранной мобильной доски с большим z-index.
    html.classList.add('btl-mobile-game-open');
    body.classList.add('btl-mobile-game-open');

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.margin = previousBodyMargin;
      html.classList.remove('btl-mobile-game-open');
      body.classList.remove('btl-mobile-game-open');
    };
  }, [responsiveLayout.fillsViewport]);


  // SFX-обёртки для UI-колбеков
  const onPointClickSfxGuarded = (realIndex) => {
    if (Date.now() <= suppressClickUntilRef.current) return;
    playSfx('click');
    longOnPointClick?.(realIndex);
  };
  const longHandleDragStartSfx = (e, fromIndex, checkerIdx) => {
    playSfx('click');
    longHandleDragStart?.(e, fromIndex, checkerIdx);
  };
  const longHandleDropSfx = (e, toIndex) => {
    playSfx('drop');
    longHandleDrop?.(e, toIndex);
  };
  const longHandleUndoMoveSfx = () => {
    playSfx('cancel');
    longHandleUndoMove?.();
  };
  const longConfirmMoveSfx = () => {
    playSfx('ok');
    longConfirmMove?.();
  };
  // const handleLongRollDiceClickSfx = () => {
  //   playSfx('dice');
  //   handleLongRollDiceClick?.();
  // };

    const handleLongRollDiceClickSfx = () => {
    // 🎲 Звук кубиков теперь играет не по клику,
    // а по фактическому появлению кубиков в состоянии longRolledDice/longFirstRolls
    handleLongRollDiceClick?.();
  };

  const [localRebuyPromptOpen, setLocalRebuyPromptOpen] = useState(false);

  const onResignSfx = () => {
    playSfx('pass'); // звуковой фидбек сдачи

    const rebuyContextFromLabels =
      getTournamentLabelClass(tournamentTypeLabel) === 'rebuy' ||
      getTournamentLabelClass(tournamentStageLabel) === 'rebuy';

    const mayNeedMobileRebuyPrompt = Boolean(
      responsiveLayout.fillsViewport &&
      (rebuyContextFromLabels || rebuyAvailable || canRebuy) &&
      (typeof onAcceptRebuy === 'function' || typeof onRebuy === 'function')
    );

    onResign?.();

    if (mayNeedMobileRebuyPrompt) {
      setLocalRebuyPromptOpen(true);
    }
  };

  /* ===================== Профили/аватарки ===================== */
  const p1 = player1Profile || {};
  const p2 = player2Profile || {};

  const p1Name = longPlayer1Name ?? p1.name ?? p1.username ?? 'Player 1';
  const p2Name = longPlayer2Name ?? p2.name ?? p2.username ?? 'Player 2';

  // === avatar utils (без двойного http) ===
  const RAW_API =
    (typeof import.meta !== 'undefined' && (import.meta.env?.VITE_API_ORIGIN || import.meta.env?.VITE_API_URL)) ||
    process.env.REACT_APP_API_ORIGIN ||
    process.env.REACT_APP_API_URL ||
    '';

  const PAGE_PROTOCOL = (typeof window !== 'undefined' && window.location?.protocol) || 'http:';
  const PAGE_HOST     = (typeof window !== 'undefined' && window.location?.hostname) || 'localhost';
  const ENV_PORT =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_PORT) ||
    process.env.REACT_APP_PORT ||
    (typeof window !== 'undefined' && window.location?.port === '3001' ? '5000' : '');

  let API_ORIGIN = '';
  if (/^https?:\/\//i.test(RAW_API)) {
    API_ORIGIN = RAW_API.replace(/\/+$/, '');
  } else if (RAW_API) {
    const hasPort = /:\d+$/.test(RAW_API);
    API_ORIGIN = `${PAGE_PROTOCOL}//${RAW_API}${hasPort ? '' : (ENV_PORT ? `:${ENV_PORT}` : '')}`.replace(/\/+$/, '');
  } else {
    API_ORIGIN = `${PAGE_PROTOCOL}//${PAGE_HOST}${ENV_PORT ? `:${ENV_PORT}` : ''}`.replace(/\/+$/, '');
  }

  const DEFAULT_AVATAR =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">
         <circle cx="48" cy="48" r="46" fill="#2b2b2b" stroke="#999" stroke-width="2"/>
         <text x="50%" y="54%" text-anchor="middle" font-family="Inter,Arial" font-size="28" fill="#ddd">BQ</text>
       </svg>`
    );

  function resolveAvatarSrc(raw) {
    const s = String(raw || '').replace(/\\/g, '/').trim();
    if (!s) return DEFAULT_AVATAR;
    if (/^https?:\/\//i.test(s)) return s;
    if (/^\/?uploads\//i.test(s) || /^\/?api\/uploads\//i.test(s)) {
      return `${API_ORIGIN}/${s.replace(/^\/+/, '')}`;
    }
    return s.startsWith('/') ? s : `/${s}`;
  }





    const formatPrize = (v) => {
    if (v == null) return '';
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString(i18n.language || 'ru')
      : String(v);
  };
  const getTournamentLabelClass = (value) => {
    const normalized = String(value || '').toLowerCase();

    if (normalized.includes('полу') || normalized.includes('semi')) {
      return 'semifinal';
    }
    if (normalized.includes('финал') || normalized.includes('final')) {
      return 'final';
    }
    if (normalized.includes('квалиф') || normalized.includes('qualif')) {
      return 'qualification';
    }
    if (
      normalized.includes('реба') ||
      normalized.includes('rebuy') ||
      normalized.includes('re-buy')
    ) {
      return 'rebuy';
    }

    return '';
  };

  const localizeTournamentLabel = (rawValue) => {
    const raw = String(rawValue || '').trim();
    const value = raw.toLowerCase();

    if (!raw) return '';
    if (value.includes('полу') || value.includes('semi')) {
      return translate('jackpotBoard.stage.semifinal');
    }
    if (value.includes('финал') || value.includes('final')) {
      return translate('jackpotBoard.stage.final');
    }
    if (value.includes('квалиф') || value.includes('qualif')) {
      return translate('jackpotBoard.stage.qualification');
    }
    if (
      value.includes('реба') ||
      value.includes('rebuy') ||
      value.includes('re-buy')
    ) {
      return translate('jackpotBoard.stage.rebuy');
    }

    return raw;
  };

  const stageClass = getTournamentLabelClass(tournamentStageLabel);
  const typeClass = getTournamentLabelClass(tournamentTypeLabel);
  const displayStageLabel = localizeTournamentLabel(tournamentStageLabel);
  const displayTournamentTypeLabel = localizeTournamentLabel(
    tournamentTypeLabel
  );

  const mobileTournamentLabels = [
    displayTournamentTypeLabel,
    displayStageLabel,
  ].filter((label, index, labels) => label && labels.indexOf(label) === index);

  const mobilePrimaryTournamentLabel = mobileTournamentLabels[0] || '';
  const mobileSecondaryTournamentLabel = mobileTournamentLabels[1] || '';

  const p1Src = resolveAvatarSrc(player1Profile?.avatar);
  const p2Src = resolveAvatarSrc(player2Profile?.avatar);

  // пробный прелоад аватарок (hook — всегда вызывается)
  useEffect(() => {
    const test = (label, url) => {
      if (!url || url === DEFAULT_AVATAR) {
        console.warn(`[AVATAR][probe] ${label}: url пустой или дефолт →`, url);
        return;
      }
      const img = new Image();
      img.onload  = () => console.log(`[AVATAR][probe] ${label}: OK 200 →`, url);
      img.onerror = (e) =>  console.warn(`[AVATAR][probe] ${label}: ERROR →`, url, e);
      img.src = url;
    };
    test('P1', p1Src);
    test('P2', p2Src);
  }, [p1Src, p2Src]);

  /* ===================== Разбиение доски ===================== */
  const boardValid = Array.isArray(longBoardState) && longBoardState.length === 24;

  const firstBlack = boardValid ? longBoardState.slice(0, 12) : [];
  const secondBlack = boardValid ? longBoardState.slice(12) : [];
  const firstWhite = boardValid ? longBoardState.slice(12) : [];
  const secondWhite = boardValid ? longBoardState.slice(0, 12).reverse() : [];

  function OffBoardStack({ checkers, color }) {
    return (
      <div className="off-stack">
        {(checkers || []).map((_, i) => (
          <div key={i} className={`checker ${color}`} />
        ))}
      </div>
    );
  }

  const fmt = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, '0');
    const s = String(sec % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  console.log('[C-4] props.longBoardState len=', Array.isArray(longBoardState) ? longBoardState.length : 'N/A');

  const diceImages = {
    white: {
      1: '/img/k1-white-1.png',
      2: '/img/k1-white-2.png',
      3: '/img/k1-white-3.png',
      4: '/img/k1-white-4.png',
      5: '/img/k1-white-5.png',
      6: '/img/k1-white-6.png',
    },
    black: {
      1: '/img/k1-black-1.png',
      2: '/img/k1-black-2.png',
      3: '/img/k1-black-3.png',
      4: '/img/k1-black-4.png',
      5: '/img/k1-black-5.png',
      6: '/img/k1-black-6.png',
    },
  };

  const isMyTurn =
    (longMyColor === 'white' && longCurrentTurn === 'player1') ||
    (longMyColor === 'black' && longCurrentTurn === 'player2');

  const canConfirmMove =
    isMyTurn &&
    longHasRolledDice &&
    longNumberMove === 0;

  /* ===================== TOUCH DRAG & DROP =====================
     Мобильные браузеры почти не поддерживают HTML5 drag/drop.
     Поэтому создаём виртуальный dataTransfer и передаём его в уже
     существующие longHandleDragStart / longHandleDrop родителя.
     Игровая логика не дублируется и остаётся в одном месте.
     ============================================================ */
  const touchDragRef = useRef({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    ghost: null,
  });
  const touchDataRef = useRef({});

  const cleanupTouchDrag = useCallback(() => {
    const current = touchDragRef.current;

    try {
      current.ghost?.remove();
    } catch (_) {}

    touchDragRef.current = {
      dragging: false,
      moved: false,
      startX: 0,
      startY: 0,
      ghost: null,
    };
  }, []);

  const createTouchDataTransfer = useCallback(() => ({
    setData(type, value) {
      const key = type || 'text/plain';
      touchDataRef.current[key] = String(value ?? '');
    },
    getData(type) {
      const key = type || 'text/plain';
      return touchDataRef.current[key] ?? '';
    },
    clearData(type) {
      if (!type) {
        touchDataRef.current = {};
      } else {
        delete touchDataRef.current[type];
      }
    },
    effectAllowed: 'move',
    dropEffect: 'move',
  }), []);

  const handleCheckerTouchStart = useCallback((event, fromIndex, checkerIndex, color) => {
    cleanupTouchDrag();
    touchDataRef.current = {};

    if (!isMyTurn) return;
    if (!longHasRolledDice) return;
    if (longNumberMove <= 0) return;
    if (String(color || '').toLowerCase() !== String(longMyColor || '').toLowerCase()) {
      return;
    }

    const touch = event.touches?.[0];
    if (!touch) return;

    const sourceElement = event.currentTarget;
    const rect = sourceElement.getBoundingClientRect();
    const ghost = document.createElement('div');

    ghost.className = `checker ${color} btl-touch-ghost`;
    ghost.style.width = `${Math.max(26, rect.width)}px`;
    ghost.style.height = `${Math.max(26, rect.height)}px`;
    ghost.style.left = `${touch.clientX}px`;
    ghost.style.top = `${touch.clientY}px`;
    document.body.appendChild(ghost);

    touchDragRef.current = {
      dragging: true,
      moved: false,
      startX: touch.clientX,
      startY: touch.clientY,
      ghost,
    };

    const syntheticDragStart = {
      type: 'dragstart',
      target: sourceElement,
      currentTarget: sourceElement,
      preventDefault() {},
      stopPropagation() {},
      dataTransfer: createTouchDataTransfer(),
    };

    longHandleDragStartSfx(
      syntheticDragStart,
      fromIndex,
      checkerIndex
    );
  }, [
    cleanupTouchDrag,
    createTouchDataTransfer,
    isMyTurn,
    longHasRolledDice,
    longNumberMove,
    longMyColor,
    longHandleDragStartSfx,
  ]);

  const handleBoardTouchMove = useCallback((event) => {
    const current = touchDragRef.current;
    const touch = event.touches?.[0];

    if (!current.dragging || !current.ghost || !touch) return;

    const distance = Math.hypot(
      touch.clientX - current.startX,
      touch.clientY - current.startY
    );

    if (distance >= 7) {
      current.moved = true;
    }

    current.ghost.style.left = `${touch.clientX}px`;
    current.ghost.style.top = `${touch.clientY}px`;

    if (current.moved) {
      event.preventDefault();
    }
  }, []);

  const handleBoardTouchEnd = useCallback((event) => {
    const current = touchDragRef.current;
    const wasDragging = current.dragging;
    const wasMoved = current.moved;
    const touch = event.changedTouches?.[0];

    if (!wasDragging) {
      cleanupTouchDrag();
      return;
    }

    // Обычный короткий тап оставляем существующей click-логике.
    if (!wasMoved || !touch) {
      cleanupTouchDrag();
      touchDataRef.current = {};
      return;
    }

    // Не допускаем дополнительного click после завершённого drag.
    suppressClickUntilRef.current = Date.now() + 450;

    const targetElement = document.elementFromPoint(
      touch.clientX,
      touch.clientY
    );
    const pointElement = targetElement?.closest?.('[data-btl-drop-index]');
    const boardElement = boardCanvasRef.current;

    if (
      pointElement &&
      boardElement?.contains(pointElement)
    ) {
      const dropIndex = Number(pointElement.dataset.btlDropIndex);

      if (Number.isInteger(dropIndex) && dropIndex >= 0 && dropIndex <= 23) {
        const syntheticDrop = {
          type: 'drop',
          target: pointElement,
          currentTarget: pointElement,
          preventDefault() {},
          stopPropagation() {},
          dataTransfer: createTouchDataTransfer(),
        };

        longHandleDropSfx(syntheticDrop, dropIndex);
      }
    }

    cleanupTouchDrag();
    touchDataRef.current = {};
  }, [cleanupTouchDrag, createTouchDataTransfer, longHandleDropSfx]);

  const handleBoardTouchCancel = useCallback(() => {
    suppressClickUntilRef.current = Date.now() + 250;
    cleanupTouchDrag();
    touchDataRef.current = {};
  }, [cleanupTouchDrag]);

  useEffect(() => cleanupTouchDrag, [cleanupTouchDrag]);

  /* ===================== Рендер стакованных шашек ===================== */
  function renderStackedCheckers(point, { isTopRow, realIndex }) {
    const checkers = Array.isArray(point) ? point : [];
    const visible  = checkers.slice(0, 5); // максимум 5
    const total    = checkers.length;

    return visible.map((checker, i) => {
      if (!checker || !checker.color) return null;
      const color = String(checker.color).toLowerCase();
      const showCount = total > 5 ? (isTopRow ? i === visible.length - 1 : i === 0) : false;

      return (
        <div
          key={i}
          className={`checker ${color}`}
          draggable="true"
          onDragStart={(e) => longHandleDragStartSfx(e, realIndex, i)}
          onTouchStart={(e) =>
            handleCheckerTouchStart(e, realIndex, i, color)
          }
          style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {showCount && <span className="stack-count">{total}</span>}
        </div>
      );
    });
  }

  /* ===================== Ряды (white/black) ===================== */
  const renderWhiteTopRow = () => (
    <div className="top-row">
      {(firstWhite || []).map((point, idx) => {
        const realIndex = 24 - idx - 1;
        return (
          <div
            key={idx}
            className={`point-container p${idx}`}
            data-btl-drop-index={realIndex}
            onClick={() => onPointClickSfxGuarded(realIndex)}
            onDragOver={longHandleDragOver}
            onDrop={(e) => longHandleDropSfx(e, realIndex)}
          >
            <span>{idx + 13}</span>
            {renderStackedCheckers(point, { isTopRow: true, realIndex })}
          </div>
        );
      })}
    </div>
  );

  const renderWhiteBottomRow = () => (
    <div className="bottom-row">
      {(secondWhite || []).map((point, idx) => {
        const realIndex = idx;
        return (
          <div
            key={idx}
            className={`point-containerd r${idx}`}
            data-btl-drop-index={realIndex}
            onClick={() => onPointClickSfxGuarded(realIndex)}
            onDragOver={longHandleDragOver}
            onDrop={(e) => longHandleDropSfx(e, realIndex)}
          >
            {renderStackedCheckers(point, { isTopRow: false, realIndex })}
            {responsiveLayout.fillsViewport ? (
              <div className="btl-bottom-number-height-control">
                <span>{12 - idx}</span>
              </div>
            ) : (
              <span>{12 - idx}</span>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderBlackTopRow = () => {
    const blackTopReversed = [...firstBlack].reverse();
    return (
      <div className="top-row">
        {blackTopReversed.reverse().map((point, idx) => {
          const realIndex = 11 - idx;
          const label = 13 + idx;
          return (
            <div
              key={idx}
              className={`point-container p${idx}`}
              data-btl-drop-index={realIndex}
              onClick={() => onPointClickSfxGuarded(realIndex)}
              onDragOver={longHandleDragOver}
              onDrop={(e) => longHandleDropSfx(e, realIndex)}
            >
              <span>{label}</span>
              {renderStackedCheckers(point, { isTopRow: true, realIndex })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderBlackBottomRow = () => {
    const blackBottomUnReversed = [...secondBlack].reverse(); // 12..23
    return (
      <div className="bottom-row">
        {blackBottomUnReversed.map((point, idx) => {
          const realIndex = 12 + idx;
          const label = 12 - idx;
          return (
            <div
              key={idx}
              className={`point-containerd r${idx}`}
              data-btl-drop-index={realIndex}
              onClick={() => onPointClickSfxGuarded(realIndex)}
              onDragOver={longHandleDragOver}
              onDrop={(e) => longHandleDropSfx(e, realIndex)}
            >
              {renderStackedCheckers(point, { isTopRow: false, realIndex })}
              {responsiveLayout.fillsViewport ? (
                <div className="btl-bottom-number-height-control">
                  <span>{label}</span>
                </div>
              ) : (
                <span>{label}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };



  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get('tableId'); // что в урле

    // если в урле нет, пробуем достать из local/sessionStorage
 const fromLongAnchor =
  localStorage.getItem('bigTournament:lastActiveTableId') ||
  sessionStorage.getItem('bigTournament:lastActiveTableId');

    const effectiveId = Number(fromUrl || fromLongAnchor);
    const username    = localStorage.getItem('username') || '';
    const token       = localStorage.getItem('token') || '';

    if (!effectiveId) {
      console.warn('[LONG] нет tableId ни в url, ни в localStorage');
      return;
    }

    // если в урле id не было — аккуратно дописываем ?tableId=...
    if (!fromUrl) {
      const nextSearch = `?tableId=${effectiveId}`;
      if (location.search !== nextSearch) {
        navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
      }
    }

    try {
      bigTournamentLongSocket.auth = { token, username };
    } catch {}

    const joinAndFetch = () => {
      const tableId = effectiveId;

      bigTournamentLongSocket.emit('long_tournament_joinTable', { tableId, username });
      bigTournamentLongSocket.emit('longTournament_joinTable',   { tableId, username });
      bigTournamentLongSocket.emit('tournamentMatch_joinTable',  { tableId, username });

      // если снапшот через ack тебе НЕ нужен — можно вообще без callback
      bigTournamentLongSocket.emit('long_fetchTournamentData', tableId, () => {});
      bigTournamentLongSocket.emit('fetchLongTournamentData',  tableId);
      bigTournamentLongSocket.emit('fetchTournamentData',      tableId);
    };

    if (bigTournamentLongSocket.connected) {
      joinAndFetch();
    } else {
      bigTournamentLongSocket.connect();
      bigTournamentLongSocket.once('connect', joinAndFetch);
    }

    bigTournamentLongSocket.on('reconnect', joinAndFetch);

    return () => {
      bigTournamentLongSocket.off('connect', joinAndFetch);
      bigTournamentLongSocket.off('reconnect', joinAndFetch);
    };
  }, [location.pathname, location.search, navigate]);


  // useEffect(() => {
  //   const qs = new URLSearchParams(location.search);
  //   const tableId  = Number(qs.get('tableId'));
  //   const username = localStorage.getItem('username') || '';
  //   const token    = localStorage.getItem('token') || '';

  //   if (!tableId) return;

  //   // 1) auth ДО connect
  //   try { bigTournamentLongSocket.auth = { token, username }; } catch {}

  //   const joinAndFetch = () => {
  //     // 2) join (несколько вариантов имён — один из них точно есть на бэке)
  //     bigTournamentLongSocket.emit('long_tournament_joinTable', { tableId, username });
  //     bigTournamentLongSocket.emit('longTournament_joinTable',   { tableId, username });
  //     bigTournamentLongSocket.emit('tournamentMatch_joinTable',  { tableId, username });

  //     // 3) запрос снимка стола
  //     // ВАРИАНТ А: через ack-колбэк — предпочтительно
  //     bigTournamentLongSocket.emit('long_fetchTournamentData', tableId, (snapshot) => {
  //       // snapshot → setState(...) твоего борда
  //       // например: setLongStateFromSnapshot(snapshot)
  //     });

  //     // ВАРИАНТ Б: если на бэке без ack — просто триггерим событие,
  //     // а слушатель у тебя уже есть ниже в компоненте
  //     bigTournamentLongSocket.emit('fetchLongTournamentData', tableId);
  //     bigTournamentLongSocket.emit('fetchTournamentData',     tableId);
  //   };

  //   // 4) если уже подключены — сразу, иначе дождаться connect
  //   if (bigTournamentLongSocket.connected) {
  //     joinAndFetch();
  //   } else {
  //     bigTournamentLongSocket.connect();
  //     bigTournamentLongSocket.once('connect', joinAndFetch);
  //   }

  //   // 5) при реконнекте — повторить (иначе после обрыва снова пусто)
  //   bigTournamentLongSocket.on('reconnect', joinAndFetch);

  //   return () => {
  //     bigTournamentLongSocket.off('connect', joinAndFetch);
  //     bigTournamentLongSocket.off('reconnect', joinAndFetch);
  //   };
  // }, [location.search, navigate]);








  /* ===================== SFX: Good luck один раз ===================== */
  useEffect(() => {
    const ready =
      Number.isInteger(longFirstRolls?.player1Roll) &&
      Number.isInteger(longFirstRolls?.player2Roll);

    if (!ready) return;
    if (goodluckPlayedRef.current) return;

    if (gestureHandlerRef.current) {
      window.removeEventListener('pointerdown', gestureHandlerRef.current);
      gestureHandlerRef.current = null;
    }

    const onFirstGesture = async () => {
      if (goodluckPlayedRef.current) return;
      try {
        const r = play?.('goodluck');
        if (r && typeof r.then === 'function') await r;
      } catch (_) {
        // ignore
      } finally {
        goodluckPlayedRef.current = true;
        suppressClickUntilRef.current = Date.now() + 250;
        window.removeEventListener('pointerdown', onFirstGesture);
        gestureHandlerRef.current = null;
      }
    };

    try {
      const maybe = play?.('goodluck');
      if (maybe && typeof maybe.then === 'function') {
        maybe.then(() => {
          goodluckPlayedRef.current = true;
          suppressClickUntilRef.current = Date.now() + 250;
        }).catch(() => {
          if (!goodluckPlayedRef.current) {
            gestureHandlerRef.current = onFirstGesture;
            window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
          }
        });
      } else {
        gestureHandlerRef.current = onFirstGesture;
        window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
      }
    } catch (_) {
      gestureHandlerRef.current = onFirstGesture;
      window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
    }

    return () => {
      if (gestureHandlerRef.current) {
        window.removeEventListener('pointerdown', gestureHandlerRef.current);
        gestureHandlerRef.current = null;
      }
    };
  }, [longFirstRolls, play]);



  // 🔊 Звук кубиков при обычных бросках (ручных и авто, своих и оппонента)
  useEffect(() => {
    const hasDice =
      Array.isArray(longRolledDice) && longRolledDice.length > 0;

    // Переход "не было кубиков" → "появились кубики"
    if (hasDice && !hadDiceRef.current) {
      hadDiceRef.current = true;
      playSfx('dice');
    }
    // Переход "кубики были" → "кубиков нет" (конец хода)
    else if (!hasDice && hadDiceRef.current) {
      hadDiceRef.current = false;
    }
  }, [longRolledDice, playSfx]);


  // 🔊 Звук кубиков при первых бросках (определение, кто ходит первым)
  useEffect(() => {
    if (!longFirstRolls) return;

    const prev = prevFirstRollsRef.current || {};
    const curr = longFirstRolls;

    const p1Changed =
      Number.isInteger(curr.player1Roll) &&
      curr.player1Roll !== prev.player1Roll;

    const p2Changed =
      Number.isInteger(curr.player2Roll) &&
      curr.player2Roll !== prev.player2Roll;

    // Если у кого-то из игроков появился новый первый бросок — играем звук
    if (p1Changed || p2Changed) {
      playSfx('dice');
    }

    prevFirstRollsRef.current = {
      player1Roll: curr.player1Roll,
      player2Roll: curr.player2Roll,
    };
  }, [longFirstRolls, playSfx]);


    useEffect(() => {
    const onConnect = () => setIsSocketDown(false);
    const onDisconnect = () => setIsSocketDown(true);
    const onConnectError = () => setIsSocketDown(true);
    const onReconnectAttempt = () => setIsSocketDown(true);
    const onReconnect = () => setIsSocketDown(false);

    bigTournamentLongSocket.on('connect', onConnect);
    bigTournamentLongSocket.on('disconnect', onDisconnect);
    bigTournamentLongSocket.on('connect_error', onConnectError);
    bigTournamentLongSocket.on('reconnect_attempt', onReconnectAttempt);
    bigTournamentLongSocket.on('reconnect', onReconnect);

    return () => {
      bigTournamentLongSocket.off('connect', onConnect);
      bigTournamentLongSocket.off('disconnect', onDisconnect);
      bigTournamentLongSocket.off('connect_error', onConnectError);
      bigTournamentLongSocket.off('reconnect_attempt', onReconnectAttempt);
      bigTournamentLongSocket.off('reconnect', onReconnect);
    };
  }, []);


  /* ===================== Рендер ===================== */
  const opponentOff = longMyColor === 'white'
    ? longBlackOffBoard
    : longWhiteOffBoard;
  const myOff = longMyColor === 'white'
    ? longWhiteOffBoard
    : longBlackOffBoard;

  const [rebuyLocallyClosed, setRebuyLocallyClosed] = useState(false);
  const [rebuySubmitting, setRebuySubmitting] = useState(false);

  const boardIndicatesFinished =
    (Array.isArray(longWhiteOffBoard) && longWhiteOffBoard.length >= 15) ||
    (Array.isArray(longBlackOffBoard) && longBlackOffBoard.length >= 15);

  const gameIsFinished = Boolean(
    gameFinished ||
    isGameFinished ||
    boardIndicatesFinished
  );

  const rebuyAction =
    typeof onAcceptRebuy === 'function'
      ? onAcceptRebuy
      : typeof onRebuy === 'function'
        ? onRebuy
        : null;

  const declineRebuyAction =
    typeof onDeclineRebuy === 'function'
      ? onDeclineRebuy
      : typeof onRejectRebuy === 'function'
        ? onRejectRebuy
        : null;

  const rebuyRequestedByParent = Boolean(
    showRebuyModal ||
    showRebuyOffer
  );

  const rebuyAvailableAfterGame = Boolean(
    rebuyAvailable ||
    canRebuy
  );

  const rebuyOfferIsActive = Boolean(
    rebuyRequestedByParent ||
    localRebuyPromptOpen ||
    (gameIsFinished && rebuyAvailableAfterGame)
  );

  const shouldShowRebuyModal = Boolean(
    rebuyOfferIsActive &&
    rebuyAction &&
    !rebuyLocallyClosed
  );

  useEffect(() => {
    // Когда предложение исчезло или началась следующая партия,
    // разрешаем показать его снова после нового окончания игры.
    if (!rebuyOfferIsActive) {
      setRebuyLocallyClosed(false);
      setRebuySubmitting(false);
    }
  }, [rebuyOfferIsActive]);

  useEffect(() => {
    if (rebuyRequestedByParent) {
      setLocalRebuyPromptOpen(false);
    }
  }, [rebuyRequestedByParent]);


  const handleAcceptRebuy = useCallback(async () => {
    if (!rebuyAction || rebuySubmitting) return;

    setRebuySubmitting(true);

    try {
      await rebuyAction();
      setLocalRebuyPromptOpen(false);
      setRebuyLocallyClosed(true);
    } catch (error) {
      console.error('[BIG LONG][REBUY] Не удалось выполнить ребай:', error);
    } finally {
      setRebuySubmitting(false);
    }
  }, [rebuyAction, rebuySubmitting]);

  const handleDeclineRebuy = useCallback(async () => {
    if (rebuySubmitting) return;

    setLocalRebuyPromptOpen(false);
    setRebuyLocallyClosed(true);

    try {
      await declineRebuyAction?.();
    } catch (error) {
      console.error('[BIG LONG][REBUY] Не удалось закрыть предложение:', error);
    }
  }, [declineRebuyAction, rebuySubmitting]);

  const shellClassName = [
    'board-wrapper',
    'long-tournament',
    'btl-game',
    responsiveLayout.isLandscape
      ? 'btl-game--landscape'
      : 'btl-game--portrait',
    responsiveLayout.isCompact
      ? 'btl-game--compact'
      : 'btl-game--desktop',
    responsiveLayout.fillsViewport
      ? 'btl-game--viewport-fill'
      : 'btl-game--contained',
  ].join(' ');

  const responsiveStyle = {
    '--btl-board-scale-x': responsiveLayout.scaleX,
    '--btl-board-scale-y': responsiveLayout.scaleY,
    '--btl-board-width': `${responsiveLayout.boardWidth}px`,
    '--btl-board-height': `${responsiveLayout.boardHeight}px`,
    '--btl-display-board-height': `${responsiveLayout.displayBoardHeight}px`,
    '--btl-slot-width': `${responsiveLayout.slotWidth}px`,
    '--btl-slot-height': `${responsiveLayout.slotHeight}px`,
  };

  const renderPlayerCard = ({
    side,
    avatar,
    name,
    rating,
    time,
    active,
  }) => {
    const numericRating = Number(rating);
    const visibleRating = Number.isFinite(numericRating)
      ? numericRating
      : '—';

    return (
      <div
        className={`btl-player-card btl-player-card--${side} ${
          active ? 'is-active' : ''
        }`}
      >
        <img
          className="btl-player-avatar"
          src={avatar || DEFAULT_AVATAR}
          alt={`${name} avatar`}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = DEFAULT_AVATAR;
          }}
        />

        <div className="btl-player-details">
          <div className="btl-player-name" title={name}>
            {name}
          </div>
          <div className="btl-player-rating">
            ELO {visibleRating}
          </div>
        </div>

        <div
          className={`btl-player-clock ${active ? 'is-active' : ''}`}
          aria-label={`${name}: ${fmt(time)}`}
        >
          {fmt(time)}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={shellRef}
      className={shellClassName}
      style={responsiveStyle}
    >
      {isSocketDown && (
        <div className="net-banner btl-net-banner">
          ⚠️ {translate('jackpotBoard.connectionLostLong')}
        </div>
      )}

      {!boardValid && (
        <div className="btl-board-error">
          {translate('jackpotBoard.invalidBoard')}
        </div>
      )}

      <header ref={hudRef} className="btl-hud">
        {renderPlayerCard({
          side: 'left',
          avatar: p1Src,
          name: p1Name,
          rating: p1.rating,
          time: player1Time,
          active: longCurrentTurn === 'player1',
        })}

        <div className="btl-hud-center">
          {(tournamentStageLabel || tournamentPrize != null) && (
            <div className="btl-tournament-meta">
              {tournamentTypeLabel &&
                displayTournamentTypeLabel !== displayStageLabel && (
                  <span className={`btl-type ${typeClass}`}>
                    {displayTournamentTypeLabel}
                  </span>
                )}
              {tournamentStageLabel && (
                <span className={`btl-stage ${stageClass}`}>
                  {displayStageLabel}
                </span>
              )}
              {tournamentPrize != null && (
                <span className="btl-prize">
                  🏅 {formatPrize(tournamentPrize)}
                </span>
              )}
            </div>
          )}

          <div className="table-actions btl-table-actions">
            {onResign && (
              <button
                type="button"
                className="btn btn--resign btl-resign-button"
                onClick={onResignSfx}
                disabled={Boolean(resignDisabled)}
                title={translate('jackpotBoard.resignTitle')}
              >
                <span className="btl-action-icon" aria-hidden="true">
                  🏳️
                </span>
                <span>{translate('jackpotBoard.resign')}</span>
              </button>
            )}

            {typeof longLeaveTable === 'function' && (
              <button
                type="button"
                className="btn btn--leave btl-lobby-button"
                onClick={longLeaveTable}
              >
                {translate('jackpotBoard.lobby')}
              </button>
            )}
          </div>
        </div>

        {renderPlayerCard({
          side: 'right',
          avatar: p2Src,
          name: p2Name,
          rating: p2.rating,
          time: player2Time,
          active: longCurrentTurn === 'player2',
        })}
      </header>

      <main className="btl-board-viewport">
        <div className="btl-board-slot">
          <div className="btl-board-scale-layer">
            <div
              ref={boardCanvasRef}
              className="board btl-board-canvas"
              onTouchMove={handleBoardTouchMove}
              onTouchEnd={handleBoardTouchEnd}
              onTouchCancel={handleBoardTouchCancel}
            >
              {/*
                Landscape V5 original BackgammonQuest skin:
                в landscape геометрия остаётся адаптивной, а внешний вид
                повторяет исходную Board33.png без растягивания картинки.
              */}
              <div
                className="btl-board-slice btl-board-slice--top"
                aria-hidden="true"
              >
                <img src="/img/Board33.png" alt="" draggable="false" />
              </div>

              <div
                className="btl-board-slice btl-board-slice--bottom"
                aria-hidden="true"
              >
                <img src="/img/Board33.png" alt="" draggable="false" />
              </div>

              <img
                ref={boardImageRef}
                className="btl-board-image"
                src="/img/Board33.png"
                alt="Board"
                draggable="false"
                onLoad={recalculateResponsiveLayout}
              />

              {responsiveLayout.fillsViewport &&
                responsiveLayout.isLandscape && (
                  <div className="btl-mobile-center-hud">
                    {renderPlayerCard({
                      side: 'center-top',
                      avatar: p1Src,
                      name: p1Name,
                      rating: p1.rating,
                      time: player1Time,
                      active: longCurrentTurn === 'player1',
                    })}

                    <div className="btl-mobile-center-info">
                      {mobilePrimaryTournamentLabel && (
                        <div className="btl-mobile-center-stage" aria-live="polite">
                          <span
                            className={`btl-mobile-center-stage-main ${
                              getTournamentLabelClass(mobilePrimaryTournamentLabel)
                            }`}
                          >
                            {mobilePrimaryTournamentLabel}
                          </span>

                          {mobileSecondaryTournamentLabel && (
                            <span
                              className={`btl-mobile-center-stage-sub ${
                                getTournamentLabelClass(mobileSecondaryTournamentLabel)
                              }`}
                            >
                              {mobileSecondaryTournamentLabel}
                            </span>
                          )}
                        </div>
                      )}

                      {onResign && (
                        <button
                          type="button"
                          className="btl-mobile-resign-icon"
                          onClick={onResignSfx}
                          disabled={Boolean(resignDisabled)}
                          aria-label={translate('jackpotBoard.resign')}
                          title={translate('jackpotBoard.resignTitle')}
                        >
                          <span aria-hidden="true">🏳️</span>
                          <span className="btl-sr-only">
                            {translate('jackpotBoard.resign')}
                          </span>
                        </button>
                      )}
                    </div>

                    {renderPlayerCard({
                      side: 'center-bottom',
                      avatar: p2Src,
                      name: p2Name,
                      rating: p2.rating,
                      time: player2Time,
                      active: longCurrentTurn === 'player2',
                    })}
                  </div>
                )}

              {longFirstRolls && (
                <div className="first-rolls-display">
                  {Number.isInteger(longFirstRolls?.player1Roll) && (
                    <img
                      src={diceImages.white[longFirstRolls.player1Roll]}
                      alt={translate('jackpotBoard.firstRollP1', {
                        value: longFirstRolls.player1Roll,
                      })}
                      className="dice-image first-roll-player1"
                      draggable="false"
                    />
                  )}
                  {Number.isInteger(longFirstRolls?.player2Roll) && (
                    <img
                      src={diceImages.black[longFirstRolls.player2Roll]}
                      alt={translate('jackpotBoard.firstRollP2', {
                        value: longFirstRolls.player2Roll,
                      })}
                      className="dice-image first-roll-player2"
                      draggable="false"
                    />
                  )}
                </div>
              )}

              {longRollEnabled && (
                <button
                  type="button"
                  className="roll-dice-button"
                  onClick={handleLongRollDiceClickSfx}
                >
                  {translate('jackpotBoard.rollDice')}
                </button>
              )}

              {Array.isArray(longRolledDice) &&
                longRolledDice.length > 0 &&
                longFirstMoveComplete && (
                  <div className="dice-display">
                    {longRolledDice.map((value, index) => {
                      const turnColor =
                        longCurrentTurn === 'player1'
                          ? 'white'
                          : 'black';

                      return (
                        <img
                          key={`${value}-${index}`}
                          src={diceImages[turnColor][value]}
                          alt={`Dice ${value}`}
                          draggable="false"
                        />
                      );
                    })}
                  </div>
                )}

              {longShowUndoButton && (
                <button
                  type="button"
                  className="undo-button"
                  onClick={longHandleUndoMoveSfx}
                >
                  {translate('jackpotBoard.undo')}
                </button>
              )}

              {canConfirmMove && (
                <button
                  type="button"
                  className="long-ok-button"
                  onClick={longConfirmMoveSfx}
                  aria-label={translate('jackpotBoard.finishMove')}
                  title={translate('jackpotBoard.finishMove')}
                >
                  {responsiveLayout.fillsViewport &&
                  responsiveLayout.isLandscape ? (
                    <span className="btl-ok-check" aria-hidden="true">
                      ✓
                    </span>
                  ) : (
                    <img
                      className="btl-ok-desktop-image"
                      src="/img/Ok.png"
                      alt=""
                      aria-hidden="true"
                      draggable="false"
                    />
                  )}
                </button>
              )}

              {longMyColor === 'white' ? (
                <>
                  {renderWhiteTopRow()}
                  {renderWhiteBottomRow()}
                </>
              ) : (
                <>
                  {renderBlackTopRow()}
                  {renderBlackBottomRow()}
                </>
              )}

              <div className="bar" />

              {/*
                Выведенные шашки большого турнира по длинным нардам.
                Отдельные btl-классы изолируют раскладку от Board.css,
                коротких нард и остальных игровых досок.
              */}
              <div className="off-board btl-off-board">
                <div className="off-board-opponent btl-off-tray btl-off-tray--opponent">
                  {(opponentOff || []).map((checker, index) => {
                    const slotIndex = Math.min(index, 14);
                    const desktopPosition = 10 + (slotIndex * 80) / 14;
                    const mobilePosition = 16 + (slotIndex * 68) / 14;
                    const color =
                      checker?.color ||
                      (longMyColor === 'white' ? 'black' : 'white');

                    return (
                      <div
                        key={`opponent-${index}`}
                        className={`checker ${color} btl-off-checker btl-off-checker--opponent`}
                        style={{
                          '--btl-off-position': `${desktopPosition}%`,
                          '--btl-off-mobile-position': `${mobilePosition}%`,
                          '--btl-off-z': index + 1,
                        }}
                        aria-hidden="true"
                      />
                    );
                  })}
                </div>

                <div className="off-board-my btl-off-tray btl-off-tray--my">
                  {(myOff || []).map((checker, index) => {
                    const slotIndex = Math.min(index, 14);
                    const desktopPosition = 90 - (slotIndex * 80) / 14;
                    const mobilePosition = 84 - (slotIndex * 68) / 14;
                    const color = checker?.color || longMyColor;

                    return (
                      <div
                        key={`mine-${index}`}
                        className={`checker ${color} btl-off-checker btl-off-checker--my`}
                        style={{
                          '--btl-off-position': `${desktopPosition}%`,
                          '--btl-off-mobile-position': `${mobilePosition}%`,
                          '--btl-off-z': index + 1,
                        }}
                        aria-hidden="true"
                      />
                    );
                  })}
                </div>
              </div>

              {!boardValid && (
                <div className="btl-board-invalid-hint">
                  {translate('jackpotBoard.invalidBoardHint')}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <div
        ref={controlsRef}
        className="auto-actions btl-auto-actions"
        role="group"
        aria-label={translate('jackpotBoard.autoSettings')}
      >
        {longFirstMoveComplete && longFirstRolls && (
          <button
            type="button"
            className={`auto-btn ${autoRoll ? 'on' : ''}`}
            onClick={onToggleAutoRoll}
            aria-pressed={Boolean(autoRoll)}
          >
            <span className="dot" aria-hidden="true" />
            {translate('jackpotBoard.autoRoll')}:{' '}
            {autoRoll
              ? translate('jackpotBoard.on')
              : translate('jackpotBoard.off')}
          </button>
        )}

        {longFirstMoveComplete && longFirstRolls && (
          <button
            type="button"
            className={`auto-btn ${autoConfirm ? 'on' : ''}`}
            onClick={onToggleAutoConfirm}
            aria-pressed={Boolean(autoConfirm)}
          >
            <span className="dot" aria-hidden="true" />
            {translate('jackpotBoard.autoConfirm')}:{' '}
            {autoConfirm
              ? translate('jackpotBoard.on')
              : translate('jackpotBoard.off')}
          </button>
        )}
      </div>

      {shouldShowRebuyModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="btl-rebuy-modal-layer" role="presentation">
            <section
              className="btl-rebuy-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="btl-rebuy-title"
              aria-describedby="btl-rebuy-description"
            >
              <div className="btl-rebuy-modal-icon" aria-hidden="true">
                ↻
              </div>

              <h2 id="btl-rebuy-title" className="btl-rebuy-modal-title">
                {rebuyTitle ||
                  translate('jackpotBoard.rebuyOfferTitle', {
                    defaultValue: 'Ребай после партии',
                  })}
              </h2>

              <p
                id="btl-rebuy-description"
                className="btl-rebuy-modal-description"
              >
                {rebuyDescription ||
                  translate('jackpotBoard.rebuyOfferDescription', {
                    defaultValue:
                      'Партия завершена. Вы можете сделать ребай и продолжить участие в турнире.',
                  })}
              </p>

              {rebuyAmount != null && (
                <div className="btl-rebuy-modal-amount">
                  {translate('jackpotBoard.rebuyOfferAmount', {
                    defaultValue: 'Стоимость ребая: {{amount}}',
                    amount: formatPrize(rebuyAmount),
                  })}
                </div>
              )}

              {rebuyToPrizeFund && (
                <div className="btl-rebuy-modal-note">
                  {translate('jackpotBoard.rebuyToPrizeFund', {
                    defaultValue: 'Сумма ребая поступает в призовой фонд.',
                  })}
                </div>
              )}

              <div className="btl-rebuy-modal-actions">
                <button
                  type="button"
                  className="btl-rebuy-modal-button btl-rebuy-modal-button--accept"
                  onClick={handleAcceptRebuy}
                  disabled={rebuySubmitting}
                >
                  {rebuySubmitting
                    ? translate('jackpotBoard.rebuyProcessing', {
                        defaultValue: 'Обработка…',
                      })
                    : rebuyAcceptLabel ||
                      translate('jackpotBoard.makeRebuy', {
                        defaultValue: 'Сделать ребай',
                      })}
                </button>

                <button
                  type="button"
                  className="btl-rebuy-modal-button btl-rebuy-modal-button--later"
                  onClick={handleDeclineRebuy}
                  disabled={rebuySubmitting}
                >
                  {rebuyDeclineLabel ||
                    translate('jackpotBoard.rebuyLater', {
                      defaultValue: 'Позже',
                    })}
                </button>
              </div>
            </section>
          </div>,
          document.body
        )}
    </div>
  );
};

export default BigTournamentBackgammonBoardDisplay;
